import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import SegmentedTabs from '../../reusable/SegmentedTabs/SegmentedTabs';
import TradeJournal from './TradeJournal';
import TwitterFeed from './TwitterFeed';
import { t } from '../../i18n/i18n';
import {
  CALENDAR_SRC,
  CHART_SRC,
  NEWS_SRC,
  NEWS_TABS,
  REPORT,
  SCREENER,
  SENTIMENT_SRC,
  TABS,
} from './data';
import './Trademate.scss';

const DARQUBE_ORIGIN = 'https://widget.darqube.com';

/**
 * A third-party widget. Keyed on its URL so switching widgets swaps the frame.
 *
 * Darqube's widgets size themselves: each posts `{ styles }` for its own frame
 * (Market Overview a 330×1040 column), and the Ticker Tape later posts its
 * measured `tickerTapeHeight` too. Darqube's embed script matches a message to
 * its frame by token — which the widget puts inside `styles`, not beside it
 * where the script looks, and leaves off `tickerTapeHeight` altogether. So a
 * message is matched here by what actually sent it: Darqube's origin, and the
 * frame's own window.
 *
 * Darqube also only serves a widget to the domain it is registered for, which
 * it reads from the referrer. The site's own `Referrer-Policy: same-origin`
 * sends it none, so the widget shows "unavailable" — a Darqube frame sends
 * its origin (never the path) instead.
 */
function Frame({ src, title }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!src.startsWith(DARQUBE_ORIGIN)) return undefined;
    const onMessage = (msg) => {
      const frame = ref.current;
      if (msg.origin !== DARQUBE_ORIGIN || !frame || msg.source !== frame.contentWindow) return;
      const { styles, tickerTapeHeight } = msg.data ?? {};
      Object.entries(styles ?? {}).forEach(([key, value]) => {
        if (key === 'token') return;
        // Only a measured height is taken. A percentage one means "fill your
        // container", which has no height of its own — the frame does
        // (`.tm-frame`) — and the Ticker Tape opens with 0px before it has
        // measured itself. Either would collapse the frame.
        if (key === 'height' && !(parseFloat(value) > 0 && !String(value).includes('%'))) return;
        frame.style.setProperty(key, value);
      });
      if (tickerTapeHeight > 0) frame.style.setProperty('height', `${tickerTapeHeight}px`);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [src]);

  return (
    <iframe
      ref={ref}
      key={src}
      className="tm-frame"
      src={src}
      title={title}
      referrerPolicy={src.startsWith(DARQUBE_ORIGIN) ? 'origin' : undefined}
      allow="clipboard-write; fullscreen"
      allowFullScreen
    />
  );
}

/**
 * A tab's card: the sub-tabs some of them have (Screener, News, Report), and
 * the widget under them.
 *
 * No heading of its own — the tablist right above already names it, and a
 * second "Market Screener" a few pixels lower only pushes the widget down.
 * The tab's name is still the card's accessible name.
 */
function Panel({ title, views, view, onView, children }) {
  return (
    <section className="tm-panel" aria-label={t(title)}>
      {views && (
        <header className="tm-panel__head">
          <SegmentedTabs
            options={views.map(({ value, label }) => ({ value, label }))}
            value={view}
            onChange={onView}
          />
        </header>
      )}
      {children}
    </section>
  );
}

/**
 * Trademate — N75, the live dashboard's market tools, widget for widget:
 * Autochartist's trade ideas, calendar, news and market report, Darqube's
 * screeners, TradingView's chart, X timelines, and the trader's own trade
 * journal. No Figma frame exists for it, so it is built
 * from the shell's own pieces: page head, a tablist, one surface card per tab.
 *
 * The tab and its sub-view live in the URL (`?tab=calendar`, `&view=twitter`)
 * so a link — from search, support or a bookmark — opens on the tool itself.
 */
export default function Trademate() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.value === params.get('tab')) ? params.get('tab') : TABS[0].value;
  const label = TABS.find((t) => t.value === tab).label;

  const pick = (list) => list.find((v) => v.value === params.get('view')) ?? list[0];
  const setTab = (value) => setParams({ tab: value }, { replace: true });
  const setView = (value) => setParams({ tab, view: value }, { replace: true });

  const body = () => {
    switch (tab) {
      case 'screener': {
        const current = pick(SCREENER);
        return (
          <Panel title={label} views={SCREENER} view={current.value} onView={setView}>
            <Frame src={current.src} title={t(current.label)} />
          </Panel>
        );
      }
      case 'calendar':
        return (
          <Panel title={label}>
            <Frame src={CALENDAR_SRC} title={t('Economic calendar')} />
          </Panel>
        );
      case 'news': {
        const current = pick(NEWS_TABS);
        // Two framed widgets and one component, so the view picks the body
        // rather than the `feed`/not-feed question the pair used to be.
        return (
          <Panel title={label} views={NEWS_TABS} view={current.value} onView={setView}>
            {current.value === 'feed' && <Frame src={NEWS_SRC} title={t('News feed')} />}
            {current.value === 'sentiment' && (
              <Frame src={SENTIMENT_SRC} title={t('News sentiment')} />
            )}
            {current.value === 'twitter' && <TwitterFeed />}
          </Panel>
        );
      }
      case 'journal':
        return <TradeJournal />;
      case 'report': {
        const current = pick(REPORT);
        return (
          <Panel title={label} views={REPORT} view={current.value} onView={setView}>
            <Frame src={current.src} title={t(current.label)} />
          </Panel>
        );
      }
      default:
        return (
          <Panel title={label}>
            <Frame src={CHART_SRC} title={t('TradingView chart')} />
          </Panel>
        );
    }
  };

  return (
    <div className="trademate">
      <header className="page-head">
        <h1 className="page-head__title">Trademate</h1>
      </header>

      <div className="trademate__tabs">
        <SegmentedTabs options={TABS} value={tab} onChange={setTab} size="lg" />
      </div>

      {body()}
    </div>
  );
}
